// What the player is fighting: a belt exam, today's shared daily exam, or
// endless Kumite (unlocked at black belt).

import { dateSeed, randomSeed } from '@/core/rng';
import { EXAMS, KUMITE } from '@/content/exams';
import { dailyRounds, ruleFor } from '@/content/daily';
import { opponentsLeft, type RoundSpec, type SimOptions } from '@/sim/sim';
import { STAGE_BPM } from '@/sim/tuning';
import type { StageId } from '@/render/stages';

/** A friend's score to beat (a challenge link); `stale` if their run was on
 *  another version of the game, so the same seed may bring other opponents. */
export interface Rival {
  score: number;
  stale?: boolean;
}

/**
 * The SIM_VERSION in which what each mode's seed brings last changed: a link
 * or a best from before it was played against other opponents or another
 * impact test (4: the senpai steps with the pad). Bump SIM_VERSION and set
 * the mode's entry here whenever that happens.
 */
export const CHANGED_IN: Record<Mode['kind'], number> = { exam: 4, daily: 4, kumite: 3 };

/** `seed` replays a given run's opponents (a daily exam's date sets its own). */
export type Mode =
  | { kind: 'exam'; exam: number; seed?: number; rival?: Rival }
  | { kind: 'daily'; date: string; rival?: Rival }
  | { kind: 'kumite'; seed?: number; rival?: Rival };

function baseRounds(mode: Mode): readonly RoundSpec[] {
  switch (mode.kind) {
    case 'exam': {
      const exam = EXAMS[mode.exam];
      if (!exam) throw new Error(`No exam ${mode.exam}`);
      return exam.rounds;
    }
    case 'daily':
      return dailyRounds(ruleFor(mode.date));
    case 'kumite':
      return [KUMITE];
  }
}

/** A match's rounds, each with the beat of the music it's fought to. */
export function roundsFor(mode: Mode): readonly RoundSpec[] {
  const stage = stageFor(mode);
  return baseRounds(mode).map((r, i) => ({ ...r, beat: 60 / bpmFor(stage, i) }));
}

/** Music tempo of a stage's round (0-based): a little faster each round. */
export function bpmFor(stage: StageId, round: number): number {
  return STAGE_BPM[stage] + STAGE_BPM.perRound * round;
}

/** Daily runs share a date seed so everyone faces the same opponents. */
export function seedFor(mode: Mode): number {
  return mode.kind === 'daily' ? dateSeed(`kiai-daily-${mode.date}`) : (mode.seed ?? randomSeed());
}

export function opponentsIn(rounds: readonly RoundSpec[]): number {
  return rounds.reduce((n, r) => n + (opponentsLeft(r) ?? 0), 0);
}

/** The daily exam's rule of the day: hearts, Kiai fill. */
export function simOptionsFor(mode: Mode): SimOptions {
  return mode.kind === 'daily' ? (ruleFor(mode.date).sim ?? {}) : {};
}

/** Red–orange exams in the dojo, green–blue in the park, purple–black and
 *  Kumite on the rooftop at night; the daily exam is out in the park unless
 *  the day's rule takes it elsewhere. */
export function stageFor(mode: Mode): StageId {
  if (mode.kind === 'kumite') return 'rooftop';
  if (mode.kind === 'daily') return ruleFor(mode.date).stage ?? 'park';
  return mode.exam <= 2 ? 'dojo' : mode.exam <= 4 ? 'park' : 'rooftop';
}
